import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Sun, Moon } from "lucide-react";
import EmiteLogo from "@/components/base/EmiteLogo";
import { saasSignInUrl } from "@/lib/app";
import { publicDocsUrl } from "@/lib/docs";

interface NavbarProps {
  darkMode: boolean;
  onToggleDark: () => void;
}

export default function Navbar({ darkMode, onToggleDark }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { label: "Producto", href: "/#producto" },
    { label: "Docs", href: publicDocsUrl() },
    { label: "Precios", href: "/precios" },
    { label: "Contadores", href: "/contadores" },
    { label: "Estado", href: "/status" },
  ];

  const isHome = location.pathname === "/";

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || !isHome
          ? darkMode
            ? "bg-slate-950/95 backdrop-blur-md border-b border-slate-800"
            : "bg-white/95 backdrop-blur-md border-b border-slate-100"
          : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 cursor-pointer">
          <EmiteLogo size={32} className="text-blue-800 dark:text-blue-400" />
          <span className="font-bold text-lg text-blue-800 dark:text-blue-400 whitespace-nowrap">
            emite.do
          </span>
        </Link>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) =>
            link.href.startsWith("http") ? (
              <a
                key={link.label}
                href={link.href}
                className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  scrolled || !isHome
                    ? darkMode
                      ? "text-slate-300 hover:text-white"
                      : "text-slate-600 hover:text-slate-900"
                    : "text-white/80 hover:text-white"
                }`}
              >
                {link.label}
              </a>
            ) : (
              <Link
                key={link.label}
                to={link.href}
                className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  scrolled || !isHome
                    ? darkMode
                      ? "text-slate-300 hover:text-white"
                      : "text-slate-600 hover:text-slate-900"
                    : "text-white/80 hover:text-white"
                }`}
              >
                {link.label}
              </Link>
            )
          )}
        </div>

        {/* Actions */}
        <div className="hidden md:flex items-center gap-3">
          <button
            onClick={onToggleDark}
            className={`w-9 h-9 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
              darkMode
                ? "text-slate-300 hover:bg-slate-800"
                : scrolled || !isHome
                ? "text-slate-500 hover:bg-slate-100"
                : "text-white/70 hover:bg-white/10"
            }`}
          >
            {darkMode ? <Sun size={16} /> : <Moon size={16} />}
          </button>
          <a
            href={saasSignInUrl()}
            className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
              scrolled || !isHome
                ? darkMode
                  ? "text-slate-300 hover:text-white"
                  : "text-slate-600 hover:text-slate-900"
                : "text-white/80 hover:text-white"
            }`}
          >
            Iniciar sesión
          </a>
          <Link
            to="/signup"
            className="bg-blue-800 text-white text-sm font-semibold px-5 py-2 rounded-full hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
          >
            Registrar empresa
          </Link>
        </div>

        {/* Mobile menu button */}
        <button
          className="md:hidden cursor-pointer text-slate-600 dark:text-slate-300"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>

      {/* Mobile menu */}
      {mobileOpen && (
        <div
          className={`md:hidden px-4 pb-4 pt-2 border-t space-y-2 ${
            darkMode ? "bg-slate-950 border-slate-800" : "bg-white border-slate-100"
          }`}
        >
          {navLinks.map((link) =>
            link.href.startsWith("http") ? (
              <a
                key={link.label}
                href={link.href}
                onClick={() => setMobileOpen(false)}
                className={`block py-2 text-sm font-medium cursor-pointer ${
                  darkMode ? "text-slate-300 hover:text-white" : "text-slate-600 hover:text-slate-900"
                }`}
              >
                {link.label}
              </a>
            ) : (
              <Link
                key={link.label}
                to={link.href}
                onClick={() => setMobileOpen(false)}
                className={`block py-2 text-sm font-medium cursor-pointer ${
                  darkMode ? "text-slate-300 hover:text-white" : "text-slate-600 hover:text-slate-900"
                }`}
              >
                {link.label}
              </Link>
            )
          )}
          <a
            href={saasSignInUrl()}
            onClick={() => setMobileOpen(false)}
            className={`block py-2 text-sm font-medium cursor-pointer ${
              darkMode ? "text-slate-300 hover:text-white" : "text-slate-600 hover:text-slate-900"
            }`}
          >
            Iniciar sesión
          </a>
          <Link
            to="/signup"
            onClick={() => setMobileOpen(false)}
            className="block w-full text-center bg-blue-800 text-white text-sm font-semibold px-5 py-2.5 rounded-full cursor-pointer mt-2"
          >
            Registrar empresa
          </Link>
        </div>
      )}
    </header>
  );
}